import { useCallback, useEffect, useState } from "react";
import {
  Breadcrumb, Button, Card, Col, Form, ListGroup, Row
} from "react-bootstrap";
import { useStateMachine } from "little-state-machine";
import {
  Divider, NoData, Spinner, Stepper
} from "@whitespace/whitespace-ui";
import { Link, useNavigate } from "react-router-dom";
import { assignmentStepsWhitespace } from "modules/assignmentSteps";
import { useForm } from "react-hook-form";
import { updateAssignWhitespacePermissionsRequest } from "actions/updateAssignWhitespacePermissionsRequest";
import { FlowOrigination } from "interfaces/FlowOrigination";
import IAuthority from "interfaces/models/IAuthority";
import { useAmplifyREST } from "hooks/useAmplifyREST";
import IClaimWhitespace from "interfaces/responses/IClaimWhitespace";
import { mapWhitespaceClaimToAuthority } from "mappers/mapWhitespaceClaimToAuthority/mapWhitespaceClaimToAuthority";
import { ClaimType } from "interfaces/models/ClaimType";
import AmplifyError from "components/AmplifyError/AmplifyError";
import IAssignWhitespacePermissionsRequest from "interfaces/requests/IAssignWhitespacePermissionsRequest";
import UserAuthorityItem from "./UserAuthortiyItem/UserAuthorityItem";

interface AssignWhitespaceUserAuthoritiesProps {
  flowOrigination : FlowOrigination
}

const AssignWhitespaceUserAuthorities = ({ flowOrigination } : AssignWhitespaceUserAuthoritiesProps) => {
  const navigate = useNavigate();

  const { actions } = useStateMachine({
    updateAssignWhitespacePermissionsRequest
  });

  const { state } = useStateMachine();
  const [anyAuthoritiesSelected, setAnyAuthoritiesSelected] = useState(false);
  const [authorities, setAuthorities] = useState<IAuthority[] | undefined>(undefined);
  const [ignoreClaimRemoval, setIgnoreClaimRemoval] = useState(false);

  const { assignWhitespacePermissionsRequest } = state;

  const {
    register, handleSubmit, getValues, setValue, watch
  } = useForm({
    mode: "onSubmit"
  });

  const {
    isSending: allocateIsSending, errors: allocateErrors, statusCode: allocateStatusCode, sendRequest: allocateSendRequest, resetState: allocateResetState
  } = useAmplifyREST();

  const {
    isSending: authoritiesIsSending, errors: authoritiesErrors, data: authoritiesData, sendRequest: authoritiesSendRequest
  } = useAmplifyREST();

  const {
    isSending: addClaimsIsSending, errors: addClaimsErrors, statusCode: addClaimsStatusCode, sendRequest: addClaimsSendRequest
  } = useAmplifyREST();

  const {
    isSending: removeClaimsIsSending, errors: removeClaimsErrors, statusCode: removeClaimsStatusCode, sendRequest: removeClaimsSendRequest
  } = useAmplifyREST();

  const apiErrors = authoritiesErrors || removeClaimsErrors || addClaimsErrors || allocateErrors;
  const isSending = authoritiesIsSending || allocateIsSending || addClaimsIsSending || removeClaimsIsSending;
  const watchFields = watch(["authorities"]);

  useEffect(() => {
    const authoritiesSelected = getValues("authorities") as number[];
    const areAuthoritiesSelected = (authoritiesSelected && authoritiesSelected.length > 0);
    setAnyAuthoritiesSelected(areAuthoritiesSelected);
  }, [watchFields]);

  useEffect(() => {
    const roleIds = state.assignWhitespacePermissionsRequest!.roleIds.map(roleId => (`roles=${roleId}&`)).join("");
    // eslint-disable-next-line max-len
    const url = `apps/${encodeURIComponent(state.selectedUserApplicationDetails!.applicationDetails.applicationId)}/claim_types/authorities/claims?${roleIds}`;
    authoritiesSendRequest(url, "GET", null);
  }, [authoritiesSendRequest]);

  useEffect(() => {
    if (authoritiesData && assignWhitespacePermissionsRequest) {
      setAuthorities(
        authoritiesData.map(
          (claim : IClaimWhitespace) => mapWhitespaceClaimToAuthority(claim)
        )
      );

      if (authoritiesData.length === 1 && assignWhitespacePermissionsRequest.authorityIds.length === 0) {
        const req :IAssignWhitespacePermissionsRequest = { ...state.assignWhitespacePermissionsRequest! };
        req.authorityIds = [authoritiesData[0].id];
        actions.updateAssignWhitespacePermissionsRequest(req);
        setAnyAuthoritiesSelected(true);
      }
    }
  }, [authoritiesData, assignWhitespacePermissionsRequest]);

  const getClaimsToRemove = () : IClaimWhitespace[] => {
    const claimsToRemove = state.assignedClaimsWhitespace!.filter((claim : IClaimWhitespace) => claim.type === ClaimType.Role
    && !assignWhitespacePermissionsRequest!.roleIds.includes(claim.id));

    claimsToRemove.push(...state.assignedClaimsWhitespace!.filter((claim : IClaimWhitespace) => claim.type === ClaimType.Authority
    && !assignWhitespacePermissionsRequest!.authorityIds.includes(claim.id)));

    return claimsToRemove;
  };

  const renderAuthorities = () => authorities?.map((authority : IAuthority, idx : number) => (
    <UserAuthorityItem register={register} key={`userAuthorityItem_${idx}`} authority={authority} />
  ));

  const constructRequestFromForm = useCallback(() => {
    const authoritiesSelected = getValues("authorities") as number[];
    if (authoritiesSelected) {
      if (typeof authoritiesSelected === "string") {
        assignWhitespacePermissionsRequest!.authorityIds.push(authoritiesSelected);
      } else {
        assignWhitespacePermissionsRequest!.authorityIds = [];
        authoritiesSelected.forEach((authorityId : number) => {
          assignWhitespacePermissionsRequest!.authorityIds.push(+authorityId);
        });
      }
    }
  }, [getValues]);

  const setControlValues = useCallback(() => {
    if (state.assignWhitespacePermissionsRequest) {
      setValue("authorities", state.assignWhitespacePermissionsRequest.authorityIds.map(String));
    }
  }, [setValue]);

  useEffect(() => {
    setControlValues();
  }, [setControlValues]);

  const toggleAllocated = () => {
    const method = "PUT";
    // eslint-disable-next-line max-len
    const url = `users/${encodeURIComponent(state.selectedUser!.userId)}/apps/${encodeURIComponent(state.selectedUserApplicationDetails!.applicationDetails.applicationId)}`;
    allocateSendRequest(url, method);
  };

  const removeClaims = () => {
    const claimsToRemove = getClaimsToRemove();

    if (claimsToRemove.length > 0) {
    // eslint-disable-next-line max-len
      const url = `apps/${encodeURIComponent(state.selectedUserApplicationDetails!.applicationDetails.applicationId)}/users/${encodeURIComponent(state.selectedUser!.userId)}/claims`;
      removeClaimsSendRequest(url, "DELETE", claimsToRemove);
    } else {
      setIgnoreClaimRemoval(true);
    }
  };

  const addClaims = (claims : IClaimWhitespace[]) => {
    // eslint-disable-next-line max-len
    const url = `apps/${encodeURIComponent(state.selectedUserApplicationDetails!.applicationDetails.applicationId)}/users/${encodeURIComponent(state.selectedUser!.userId)}/claims`;
    addClaimsSendRequest(url, "PUT", claims);
  };

  const updateClaims = () => {
    const { assignedClaimsWhitespace } = state;

    const selectedAuthoryClaims :IClaimWhitespace[] = authoritiesData
      .filter((claim : IClaimWhitespace) => state.assignWhitespacePermissionsRequest?.authorityIds.includes(claim.id));
    const selectedRoleClaims :IClaimWhitespace[] = state.currentWhitespaceClaimsRoles!
      .filter((claim : IClaimWhitespace) => state.assignWhitespacePermissionsRequest?.roleIds.includes(claim.id));
    assignedClaimsWhitespace!.push(...selectedRoleClaims);
    assignedClaimsWhitespace!.push(...selectedAuthoryClaims);

    const claimsToAdd = assignedClaimsWhitespace!.filter(
      (item, i, arr) => arr.findIndex((t : IClaimWhitespace) => t.id === item.id && t.type === item.type) === i
    );
    removeClaims();
    addClaims(claimsToAdd);
  };

  useEffect(() => {
    if ((removeClaimsStatusCode === 204 || ignoreClaimRemoval) && addClaimsStatusCode === 204 && allocateStatusCode === 204) {
      if (flowOrigination === FlowOrigination.UserManagement) {
        navigate("/manageuserapplications");
      } else {
        navigate("/requests");
      }
    }
  }, [removeClaimsStatusCode, addClaimsStatusCode, allocateStatusCode]);

  useEffect(() => {
    if (addClaimsStatusCode === 204 && !addClaimsIsSending) {
      removeClaims();
    }
  }, [addClaimsStatusCode, addClaimsIsSending]);

  const handleSaveClick = () => {
    constructRequestFromForm();
    updateClaims();
    toggleAllocated();
  };

  const handleBackButtonClick = () => {
    if (flowOrigination === FlowOrigination.UserManagement) {
      navigate("/assignuserwhitespaceroles");
    } else {
      navigate("/requestsassignuserwhitespaceroles");
    }
  };

  return (
    <>

      {apiErrors && <AmplifyError errors={apiErrors} />}

      <Card>
        <Card.Header>
          <Row className="w-100">
            <Col xs={8}>
              <h1>
                Assign Authorities
                <Spinner
                  animation="grow"
                  visible={isSending}
                  spinnerText="Loading"
                  variant="info"
                />
              </h1>
            </Col>
            <Col xs={4} className="d-flex justify-content-end">
              <span className="selected-user-title">{state.selectedUser?.username}</span>
            </Col>
          </Row>
        </Card.Header>
        <Card.Body className="no-padding">
          <Form noValidate onSubmit={handleSubmit(handleSaveClick)}>

            <Row className="d-flex w-100 align-items-center top-margin-small bottom-margin-small">
              <Col xs={8} className="d-flex">
                <Breadcrumb className="breadcrumb-manage-user left-margin-large no-wrap">
                  {flowOrigination === FlowOrigination.Requests
            && (
              <>
                <Breadcrumb.Item
                  data-testid="breadcrumb-requests"
                  linkAs={Link}
                  linkProps={{ to: "/requests" }}
                  onClick={() => navigate("/requests")}
                >
                  Requests
                </Breadcrumb.Item>
                <Breadcrumb.Item active>
                  {state.selectedUserApplicationDetails!.applicationDetails.siteUrl}
                </Breadcrumb.Item>
                <Breadcrumb.Item
                  onClick={() => navigate("/requestsassignuserwhitespaceroles")}
                >
                  Roles
                </Breadcrumb.Item>
                <Breadcrumb.Item active>
                  Authorities
                </Breadcrumb.Item>
              </>
            )}
                  {flowOrigination === FlowOrigination.UserManagement
            && (
              <>
                <Breadcrumb.Item
                  data-testid="breadcrumb-users"
                  linkAs={Link}
                  linkProps={{ to: "/users" }}
                  onClick={() => navigate("/users")}
                >
                  Users
                </Breadcrumb.Item>

                <Breadcrumb.Item
                  data-testid="breadcrumb-manage-users"
                  linkAs={Link}
                  linkProps={{ to: "/manageuser" }}
                  onClick={() => navigate("/manageuser")}
                >
                  Manage User
                </Breadcrumb.Item>
                <Breadcrumb.Item
                  data-testid="breadcrumb-manage-user-applications"
                  linkAs={Link}
                  linkProps={{ to: "/manageuserapplications" }}
                  onClick={() => navigate("/manageuserapplications")}
                >
                  Applications
                </Breadcrumb.Item>
                <Breadcrumb.Item active>
                  {state.selectedUserApplicationDetails!.applicationDetails.siteUrl}

                </Breadcrumb.Item>
                <Breadcrumb.Item
                  data-testid="breadcrumb-roles"
                  linkAs={Link}
                  linkProps={{ to: "/assignuserwhitespaceroles" }}
                  onClick={() => navigate("/assignuserwhitespaceroles")}
                >
                  Roles
                </Breadcrumb.Item>

                <Breadcrumb.Item active>
                  Authorities
                </Breadcrumb.Item>

              </>
            )}
                </Breadcrumb>
              </Col>
              <Col className="d-flex justify-content-end">
                <Button variant="primary" type="submit" disabled={!anyAuthoritiesSelected || isSending}>
                  Save
                </Button>
              </Col>
            </Row>
            <Divider />
            <Row className="d-flex w-100 align-items-center top-margin-small bottom-margin-small">
              <Col xs={8} className="d-flex">
                <Stepper className="w-100 ms-4 mb-0" steps={assignmentStepsWhitespace} currentStep={1} />
              </Col>
              <Col xs={4} className="d-flex justify-content-end">
                <Button variant="secondary" onClick={() => handleBackButtonClick()}>
                  Back
                </Button>

              </Col>
            </Row>
            <Divider />
            {authorities
          && (
            <ListGroup variant="flush" className="striped highlight">
              <NoData visible={authorities.length === 0} description="No Authorities Found" />
              {renderAuthorities()}
            </ListGroup>
          )}
          </Form>
        </Card.Body>
      </Card>
    </>
  );
};

export default AssignWhitespaceUserAuthorities;
